import { useEffect } from "react";
import {
  trackSectionView,
  trackSectionEngagement,
} from "@/lib/analytics";

/**
 * Umbral para considerar que una sección está "viéndose":
 * al menos el 50 % del elemento debe ser visible.
 */
const VISIBILITY_THRESHOLD = 0.5;

/**
 * Tiempo mínimo que la sección debe estar visible antes de contabilizar
 * un section_view (evita contar scrolls rápidos que solo la cruzan).
 */
const MIN_VISIBLE_MS = 1000;

interface Timers {
  /** setTimeout que arranca el evento section_view tras MIN_VISIBLE_MS */
  viewTimer: number | null;
  /** timestamp en el que empezó a estar visible (para calcular engagement) */
  visibleSince: number | null;
  /** Si ya se ha registrado un section_view en esta racha visible */
  viewFired: boolean;
}

/**
 * Trackea automáticamente qué secciones de la landing ve el usuario
 * y cuánto tiempo pasa en cada una.
 *
 * Uso:
 *   1. En cada <section> añadir el atributo data-section="mi-seccion".
 *   2. Montar el hook en el componente raíz de la landing (Index.tsx):
 *        useSectionTracking();
 *
 * Cómo funciona:
 * - IntersectionObserver detecta cuando ≥ 50 % de la sección entra/sale
 *   del viewport.
 * - Cuando entra, arranca un timer de 1 s. Si sigue visible al terminar,
 *   dispara "section_view" en GA4.
 * - Cuando sale (o cuando el componente se desmonta), dispara
 *   "section_engagement" con el tiempo total que ha estado visible.
 *
 * Los eventos incluyen automáticamente el idioma (via user_property
 * "language" configurada por useLanguageTracking).
 */
export const useSectionTracking = (): void => {
  useEffect(() => {
    // Estado por sección, indexado por el valor de data-section
    const state = new Map<Element, Timers>();

    // Localizar todas las secciones marcadas
    const sections = Array.from(
      document.querySelectorAll<HTMLElement>("[data-section]")
    );

    if (sections.length === 0) return;

    // Inicializar el estado
    sections.forEach((el) => {
      state.set(el, {
        viewTimer: null,
        visibleSince: null,
        viewFired: false,
      });
    });

    /**
     * Descarga el evento de engagement al salir del viewport
     * (o al desmontar / cambiar de pestaña).
     */
    const flushEngagement = (el: Element) => {
      const timers = state.get(el);
      if (!timers) return;
      const sectionName = (el as HTMLElement).dataset.section || "unknown";

      if (timers.viewTimer !== null) {
        window.clearTimeout(timers.viewTimer);
        timers.viewTimer = null;
      }

      if (timers.visibleSince !== null) {
        const elapsed = Date.now() - timers.visibleSince;
        if (timers.viewFired && elapsed > 0) {
          trackSectionEngagement(sectionName, elapsed);
        }
        timers.visibleSince = null;
      }
      timers.viewFired = false;
    };

    // IntersectionObserver: dispara al cruzar el umbral (both ways)
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const el = entry.target;
          const timers = state.get(el);
          if (!timers) return;
          const sectionName = (el as HTMLElement).dataset.section || "unknown";

          if (
            entry.isIntersecting &&
            entry.intersectionRatio >= VISIBILITY_THRESHOLD
          ) {
            // Empieza a ser visible
            if (timers.visibleSince === null) {
              timers.visibleSince = Date.now();
            }
            // Programa el section_view tras 1 s de visibilidad continua
            if (timers.viewTimer === null && !timers.viewFired) {
              timers.viewTimer = window.setTimeout(() => {
                trackSectionView(sectionName);
                timers.viewFired = true;
                timers.viewTimer = null;
              }, MIN_VISIBLE_MS);
            }
          } else {
            // Deja de ser visible
            flushEngagement(el);
          }
        });
      },
      {
        // Doble threshold para detectar entradas y salidas del umbral
        threshold: [0, VISIBILITY_THRESHOLD, 1],
      }
    );

    sections.forEach((el) => observer.observe(el));

    /**
     * Cuando el usuario oculta la pestaña o cierra la web, hay que
     * descargar el engagement en marcha o se pierde ese tiempo.
     */
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        sections.forEach(flushEngagement);
      }
    };
    const handleBeforeUnload = () => {
      sections.forEach(flushEngagement);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", handleBeforeUnload);

    return () => {
      // Al desmontar (navegación a otra página), flush todo
      sections.forEach(flushEngagement);
      observer.disconnect();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", handleBeforeUnload);
    };
  }, []);
};